import type { WingmanClientOptions } from "./client";
import type { WingmanRouting } from "./routing";
import { trimTrailingSlash, wingmanLegacyBaseUrl, wingmanV2BaseUrl } from "./urls";

/**
 * Same-origin entry point for signed-in Wingman traffic. The route handler
 * behind it decrypts the httpOnly session cookie on the server and forwards
 * `Authorization: Bearer` to Wingman without the cookie. Responses are
 * streamed, so artifact downloads and chat streams of any size pass through;
 * request bodies are capped by Vercel at 4.5 MB, which is why attachment
 * bytes never travel this way (see `attachmentUploads` below).
 */
export const WINGMAN_BFF_ROOT = "/api/wingman";

export type WingmanBffKind = "v2" | "legacy";

export function wingmanBffKind(
  routing: Pick<WingmanRouting, "apiVersion">
): WingmanBffKind {
  return routing.apiVersion === "v2" ? "v2" : "legacy";
}

export type SignedInWingmanTransportOptions = {
  /**
   * The chat send carries attachments. On v2 this changes nothing here: the
   * adapter uploads each file straight to Wingman under a single-use grant
   * and the append body stays small. Legacy Wingman has no grant flow, so
   * its (streaming) append with inlined files keeps going directly to the
   * legacy host with the session cookie, exactly as before the BFF.
   */
  largeBody?: boolean;
};

/**
 * Same-origin BFF base the browser calls. The JWT never reaches page
 * JavaScript.
 */
export function wingmanBffBaseUrl(routing: Pick<WingmanRouting, "apiVersion">): string {
  return `${WINGMAN_BFF_ROOT}/${wingmanBffKind(routing)}`;
}

export function signedInWingmanTransport(
  routing: WingmanRouting,
  options: SignedInWingmanTransportOptions = {}
): Pick<
  WingmanClientOptions,
  "serverURL" | "apiVersion" | "attachmentUploads" | "largeBodyServerURL"
> {
  if (routing.apiVersion === "v2") {
    return {
      serverURL: wingmanBffBaseUrl(routing),
      apiVersion: routing.apiVersion,
      attachmentUploads: "grant",
    };
  }
  return {
    serverURL: wingmanBffBaseUrl(routing),
    apiVersion: routing.apiVersion,
    ...(options.largeBody ? { largeBodyServerURL: routing.serverURL } : {}),
  };
}

export function isWingmanBffKind(kind: string): kind is WingmanBffKind {
  return kind === "v2" || kind === "legacy";
}

/**
 * Allowlisted upstream for a BFF kind. Hosts come only from env (and the
 * Europe v2 default), never from the request, so the proxy cannot be
 * steered at an arbitrary origin.
 */
export function resolveWingmanBffUpstream(kind: WingmanBffKind): string | null {
  const url = kind === "v2" ? wingmanV2BaseUrl() : wingmanLegacyBaseUrl();
  const trimmed = trimTrailingSlash(url);
  return trimmed === "" ? null : trimmed;
}

/**
 * Turn already-decoded path segments into the upstream path suffix.
 *
 * The upstream host is fixed by the allowlist, but the base path (`/v2`)
 * would still be escapable if a segment decoded to `..` or smuggled a
 * separator: `%2e%2e` arrives from Next as `..`, `encodeURIComponent` leaves
 * dots alone, and `fetch` normalizes `/v2/../x` to `/x`. Dot segments and
 * segments containing a separator are therefore rejected outright, and
 * every surviving segment is re-encoded so nothing the upstream decodes can
 * re-introduce one. Returns `null` when any segment is unsafe.
 */
export function safeUpstreamPath(segments: readonly string[]): string | null {
  if (segments.length === 0) return null;
  const encoded: string[] = [];
  for (const segment of segments) {
    if (segment === "" || segment === "." || segment === ".." || /[/\\]/.test(segment)) {
      return null;
    }
    encoded.push(encodeURIComponent(segment));
  }
  return encoded.join("/");
}
